import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Drawer, Fade, Paper } from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid } from '@mui/x-data-grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MDBox from 'components/MDBox';
import {
  useBulkDeleteAssistantsMutation,
  useCreateAssistantMutation,
  useGetAssistantsQuery,
  useUpdateAssistantMutation
} from 'services/assistantApi';
import './assistants.css';
import AssistantDetails from 'pages/assistants/admin/components/assistantsDetails';
import AssistantCustomToolbar from 'pages/assistants/admin/components/assistantCustomToolbar';
import { useNotification } from 'hook/useNotification';
import MDButton from 'components/MDButton';

/**
 * Component for managing Assistants, including displaying a list of assistants, adding, updating,
 * duplicating, and deleting assistants. Uses a DataGrid for listing the assistants and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <Assistants />
 *
 * @returns {React.Component} The Assistants management component.
 */
export default function AdminAssistants() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const {
    data: assistants,
    error: assistantsError,
    isLoading: assistantsLoading
  } = useGetAssistantsQuery();
  const [bulkDeleteAssistant] = useBulkDeleteAssistantsMutation();
  const [createAssistant] = useCreateAssistantMutation();
  const [updateAssistant] = useUpdateAssistantMutation();

  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const processedAssistants = (assistants || []).map((assistant) => ({
    ...assistant,
    creationDate: new Date(assistant.creationDate).toLocaleDateString()
  }));

  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.assistants.table.column.id` }),
      width: 50,
      disableColumnMenu: true
    },
    { field: 'code', headerName: msg({ id: `page.assistants.table.column.code` }), minWidth: 350 },
    {
      field: 'label',
      headerName: msg({ id: `page.assistants.table.column.label` }),
      minWidth: 300,
      flex: 1
    },
    {
      field: 'isPublic',
      headerName: msg({ id: `page.assistants.table.column.isPublic` }),
      width: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      renderCell: (params) =>
        params.value ? (
          <CheckCircleIcon color="success" fontSize="medium" />
        ) : (
          <CancelIcon color="disabled" />
        )
    },
    {
      field: 'creationDate',
      headerName: msg({ id: `page.assistants.table.column.creationDate` }),
      minWidth: 150
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedAssistant(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedAssistant(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeleteAssistant(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.assistants.delete.success' })
          });
          clearAssistantSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.assistants.delete.error' })
          });
        });
    }
  };

  const handleDuplicate = () => {
    const toDuplicate = selectedRows[0];
    if (toDuplicate) {
      setSelectedAssistant({ ...selectedRows[0], id: null, code: `${toDuplicate.code}_COPY` });
      setDetailDrawerOpen(true);
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const selectedRows = rowSelectionModel.map((id) =>
    assistants.find((assistant) => assistant.id === id)
  );

  if (assistantsError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.assistant.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }}>
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            rows={processedAssistants}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: assistants && assistants.length > 0 ? [assistants[0].id] : [] },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'code', sort: 'asc' }]
              }
            }}
            slots={{ toolbar: AssistantCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                onDuplicate: handleDuplicate,
                selectedRows
              }
            }}
            loading={assistantsLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <AssistantDetails
            assistant={selectedAssistant}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearAssistantSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedAssistant(null);
    setSelectedRowId(null);
  }

  function prepareAssistantDataForSubmit(data) {
    return {
      ...data,
      users: data?.users.map((user) => user?.id),
      apiKeys: data?.apiKeys.map((apiKey) => apiKey?.id)
    };
  }

  function handleCreate(data) {
    createAssistant(prepareAssistantDataForSubmit(data))
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.assistants.create.success' })
        });
        clearAssistantSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.assistants.create.error' })
        });
      });
  }

  function handleUpdate(data) {
    updateAssistant({
      assistantId: data.id,
      assistantRequest: prepareAssistantDataForSubmit(data)
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.assistants.update.success' })
        });
        clearAssistantSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.assistants.update.error' })
        });
      });
  }
}
